import { 
  DashboardProductResultDto,
  DashboardMainTopDto,
  DashboardKpiHistoryDto,
} from './Dto_Home';
import { formatYearMonth } from '../../../utils/date/dateUtils';
import { formatNumber } from '../../../utils/number/numberUtils';
import { 
  generateColors, 
  generateChartColors,
  getEmptyChartData,
} from '../../../utils/chart/chartUtils';

/**
 * 월별 생산량 혼합 차트 데이터 (Line + Bar)
 */
export const getMixedChartData = (monthlyProductionData: DashboardProductResultDto[]) => {
  const groupedByMonthAndItem: { [key: string]: DashboardProductResultDto[] } = {};
  monthlyProductionData.forEach(item => {
    if (!groupedByMonthAndItem[item.groupKey]) {
      groupedByMonthAndItem[item.groupKey] = [];
    }
    groupedByMonthAndItem[item.groupKey].push(item);
  });

  const months = Object.keys(groupedByMonthAndItem).sort();
  const products = Array.from(new Set(monthlyProductionData.map(item => item.codeNm)));

  const datasets = [
    // 각 제품별 라인 차트
    ...products.map((product, index) => {
      const colors = [
        'rgb(75, 192, 192)',
        'rgb(255, 99, 132)',
        'rgb(255, 206, 86)',
        'rgb(54, 162, 235)',
        'rgb(153, 102, 255)'
      ];
      
      const data = months.map(month => {
        const item = groupedByMonthAndItem[month]?.find(d => d.codeNm === product);
        return item ? item.productQty : 0;
      });

      return {
        type: 'line' as const,
        label: product,
        data,
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length].replace('rgb', 'rgba').replace(')', ', 0.2)'),
        tension: 0.1,
      };
    }),
    // 전체 생산량 바 차트
    {
      type: 'bar' as const,
      label: '월별 전체 생산량',
      data: months.map(month => 
        groupedByMonthAndItem[month]?.reduce((sum, item) => sum + item.productQty, 0) || 0
      ),
      backgroundColor: 'rgba(54, 162, 235, 0.5)',
      borderColor: 'rgb(54, 162, 235)',
      borderWidth: 1,
    }
  ];

  return {
    labels: months.map(month => month.replace('2024-', '') + '월'),
    datasets
  };
};

/**
 * 월별 불량현황 바 차트 데이터 (itemGroupData 기반 - 월별 총생산량, 불량, 입고량)
 */
export const getItemGroupChartData = (itemGroupData: DashboardProductResultDto[]) => {
  // itemGroupData를 월별로 그룹화
  const groupedByMonthAndItem: { [key: string]: DashboardProductResultDto[] } = {};
  itemGroupData.forEach(item => {
    if (!groupedByMonthAndItem[item.groupKey]) {
      groupedByMonthAndItem[item.groupKey] = [];
    }
    groupedByMonthAndItem[item.groupKey].push(item);
  });

  // 월별로 합계 계산
  const months = Object.keys(groupedByMonthAndItem).sort();
  
  return {
    labels: months.map(month => month.replace('2025-', '') + '월'),
    datasets: [
      {
        label: '총생산량',
        data: months.map(month => 
          groupedByMonthAndItem[month]?.reduce((sum, item) => sum + item.productQty, 0) || 0
        ),
        backgroundColor: 'rgba(54, 162, 235, 0.7)',
        borderColor: 'rgb(54, 162, 235)',
        borderWidth: 1,
      },
      {
        label: '불량',
        data: months.map(month => 
          groupedByMonthAndItem[month]?.reduce((sum, item) => sum + item.badQty, 0) || 0
        ),
        backgroundColor: 'rgba(255, 99, 132, 0.7)',
        borderColor: 'rgb(255, 99, 132)',
        borderWidth: 1,
      },
      {
        label: '입고량',
        data: months.map(month => {
          const monthData = groupedByMonthAndItem[month] || [];
          return monthData.reduce((sum, item) => {
            if (item.acceptQty !== undefined) {
              return sum + item.acceptQty;
            } else {
              return sum + (item.productQty - item.badQty);
            }
          }, 0);
        }),
        backgroundColor: 'rgba(75, 192, 192, 0.7)',
        borderColor: 'rgb(75, 192, 192)',
        borderWidth: 1,
      }
    ]
  };
};

/**
 * KPI 전송현황 도넛 차트 데이터 (KPI건수 vs 전송건수로 구분)
 */
export const getKpiSendStatusData = (kpiHistoryData: DashboardKpiHistoryDto[]) => {
  // console.log('🔍 KPI 이력 데이터:', kpiHistoryData);
  // console.log('🔍 KPI 이력 데이터 길이:', kpiHistoryData?.length);
  
  // 데이터가 없거나 빈 배열일 때 기본 상태 표시
  if (!kpiHistoryData || kpiHistoryData.length === 0) {
    // console.log('⚠️ KPI 이력 데이터가 없음');
    return getEmptyChartData('KPI 데이터 없음');
  }

  // 월별 KPI 현황을 도넛 차트로 표시 (각 월별로 구분)
  const monthlyData: { [key: string]: { total: number; sent: number; month: string } } = {};
  
  kpiHistoryData.forEach((item, index) => {
    // console.log(`📝 KPI 아이템 ${index}:`, item);
    
    const yearMonth = item.yearMonth || '미분류';
    const kpiTotal = item.kpiTotalCount || 0;
    const kpiSent = item.kpiSentCount || 0;
    
    // 년월을 보기 좋게 변환 (202501 → 2025년 1월)
    const formattedMonth = formatYearMonth(yearMonth);
    
    if (!monthlyData[yearMonth]) {
      monthlyData[yearMonth] = { total: 0, sent: 0, month: formattedMonth };
    }
    
    monthlyData[yearMonth].total += kpiTotal;
    monthlyData[yearMonth].sent += kpiSent;
    
    // console.log(`📊 ${formattedMonth} - 총건수: ${kpiTotal}, 전송건수: ${kpiSent}`);
  });

  // console.log('📊 월별 KPI 집계:', monthlyData);

  // 월별 데이터를 배열로 변환하고 정렬
  const sortedMonths = Object.keys(monthlyData).sort();
  const labels: string[] = [];
  const data: number[] = [];
  const colors: string[] = [];
  
  // 월별 데이터와 색상 생성
  const chartColors = generateColors(sortedMonths.length);
  
  sortedMonths.forEach((yearMonth, index) => {
    const monthInfo = monthlyData[yearMonth];
    labels.push(`${monthInfo.month} (${formatNumber(monthInfo.sent)}/${formatNumber(monthInfo.total)}건)`);
    data.push(monthInfo.sent);
  });
  
  // console.log('🏷️ 최종 라벨:', labels);
  // console.log('📊 최종 데이터:', data);

  return {
    labels,
    datasets: [{
      data,
      ...generateChartColors(chartColors),
    }]
  };
};

/**
 * 품목그룹별 생산실적 현황 도넛 차트 데이터 (batchDefectData 기반 - 품목 분류별 구분)
 */
export const getBatchDefectDoughnutData = (batchDefectData: DashboardProductResultDto[]) => {
  // console.log('🔍 batchDefectData 원본 데이터:', batchDefectData);
  
  // batchDefectData를 품목 분류(codeNm) 기준으로 그룹화
  const groupedByItem: { [key: string]: DashboardProductResultDto[] } = {};
  batchDefectData.forEach((item, index) => {
    // console.log(`📝 아이템 ${index}:`, {
    //   groupKey: item.groupKey,
    //   code: item.code,
    //   codeNm: item.codeNm,
    //   productQty: item.productQty,
    //   badQty: item.badQty
    // });
    
    const itemName = item.codeNm && item.codeNm.trim() !== '' ? item.codeNm : '분류없음';
    if (!groupedByItem[itemName]) {
      groupedByItem[itemName] = [];
    }
    groupedByItem[itemName].push(item);
  });

  // console.log('📊 그룹화된 데이터:', groupedByItem);

  // 각 품목 분류별 총 생산량 계산
  const itemSummary = Object.keys(groupedByItem).map(itemName => ({
    name: itemName,
    totalQty: groupedByItem[itemName].reduce((sum, item) => sum + item.productQty, 0),
    count: groupedByItem[itemName].length // 해당 품목의 생산 차수 개수
  }));

  // 생산량 기준으로 정렬 (내림차순)
  itemSummary.sort((a, b) => b.totalQty - a.totalQty);

  // console.log('🏆 최종 품목별 집계:', itemSummary);

  // 품목별 고정 색상 매핑
  const colorMap: { [key: string]: string } = {
    '나짱': 'rgba(255, 99, 132, 0.8)',    // 빨강
    '전주종': 'rgba(54, 162, 235, 0.8)',   // 파랑  
    '명장': 'rgba(255, 206, 86, 0.8)',     // 노랑
    '연진': 'rgba(75, 192, 192, 0.8)',     // 청록
    '전진': 'rgba(153, 102, 255, 0.8)',    // 보라
    '분류없음': 'rgba(201, 203, 207, 0.8)', // 회색
  };

  // 기본 색상 팔레트 (매핑되지 않은 품목용)
  const defaultColors = [
    'rgba(75, 192, 192, 0.8)',
    'rgba(255, 99, 132, 0.8)',
    'rgba(255, 206, 86, 0.8)',
    'rgba(54, 162, 235, 0.8)',
    'rgba(153, 102, 255, 0.8)'
  ];

  // 색상 배열 생성
  const colors = itemSummary.map((item, index) => {
    return colorMap[item.name] || defaultColors[index % defaultColors.length];
  });

  return {
    labels: itemSummary.map(item => `${item.name} (${item.totalQty.toLocaleString()}개)`),
    datasets: [{
      data: itemSummary.map(item => item.totalQty),
      backgroundColor: colors,
      borderColor: colors.map(color => 
        color.replace('rgba', 'rgb').replace(', 0.8)', ')')
      ),
      hoverBackgroundColor: colors.map(color => 
        color.replace('0.8)', '0.9)')
      ),
    }]
  };
};

/**
 * 요약 정보 계산
 */
export const getSummaryData = (
  mainTopData: DashboardMainTopDto | null,
  monthlyProductionData: DashboardProductResultDto[],
  itemGroupData: DashboardProductResultDto[],
  batchDefectData: DashboardProductResultDto[],
  kpiHistoryData: DashboardKpiHistoryDto[]
) => {
  // mainTopData가 있으면 실제 백엔드 데이터 사용
  if (mainTopData) {
    const totalProduction = mainTopData.productQty || 0;
    const totalDefects = mainTopData.badQty || 0;
    const defectRate = mainTopData.defectRate || 0;
    
    // 합격률 계산 (백엔드에서 제공되지 않는 경우)
    const acceptanceRate = mainTopData.acceptanceRate || 
      (totalProduction > 0 ? ((totalProduction - totalDefects) / totalProduction) * 100 : 100);
    
    return {
      totalProduction: totalProduction,
      totalDefects: totalDefects,
      defectRate: `${defectRate.toFixed(2)}%`,
      acceptanceRate: `${acceptanceRate.toFixed(2)}%`,
      kpiSendCount: mainTopData.kpiSendCount || 0,
      lastUpdateTime: mainTopData.lastUpdateTime || new Date().toLocaleString('ko-KR')
    };
  }

  // fallback: 기존 계산 로직
  const totalProduction = monthlyProductionData.reduce((sum, item) => sum + (item.productQty || 0), 0) +
                         itemGroupData.reduce((sum, item) => sum + (item.productQty || 0), 0) +
                         batchDefectData.reduce((sum, item) => sum + (item.productQty || 0), 0);
  
  const totalDefects = monthlyProductionData.reduce((sum, item) => sum + (item.badQty || 0), 0) +
                      itemGroupData.reduce((sum, item) => sum + (item.badQty || 0), 0) +
                      batchDefectData.reduce((sum, item) => sum + (item.badQty || 0), 0);
  
  const defectRate = totalProduction > 0 ? ((totalDefects / totalProduction) * 100).toFixed(2) : '0.00';
  const acceptanceRate = totalProduction > 0 ? (((totalProduction - totalDefects) / totalProduction) * 100).toFixed(2) : '100.00';
  
  return {
    totalProduction,
    totalDefects,
    defectRate: `${defectRate}%`,
    acceptanceRate: `${acceptanceRate}%`,
    kpiSendCount: kpiHistoryData.length || 0,
    lastUpdateTime: new Date().toLocaleString('ko-KR')
  };
}; 